// 通知面板 —— Topbar 铃铛点击展开的 popover
// - 列表：最近 20 条系统通知（kind 标签 + title + body + 相对时间 + 已读/未读样式）
// - 未读条目背景高亮；点击单条 → 标记已读 + 跳转 linkPath
// - 底部：全部标为已读（有未读时显示）
// - 点击外部关闭（Topbar 已用 click-outside，这里内部 stopPropagation）
//
// 数据复用 useNotifications（与 Topbar 铃铛徽章同一 query 缓存，TanStack 自动 dedupe）

import { Bell as BellIcon, Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '../lib/cn.js';
import {
  NOTIF_KIND_LABEL,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type NotificationDTO,
} from '../lib/notifications.js';
import { formatRelative } from '../lib/time.js';

interface Props {
  onClose: () => void;
}

export function NotificationPanel({ onClose }: Props) {
  const q = useNotifications();
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const panelRef = useRef<HTMLDivElement>(null);

  // ESC 关闭
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const items = q.data?.items ?? [];
  const unread = q.data?.unread ?? 0;

  const handleClick = (it: NotificationDTO) => {
    if (it.readAt == null) void markRead.mutate(it.id);
    if (it.linkPath) {
      void navigate(it.linkPath);
    }
    onClose();
  };

  return (
    <div
      ref={panelRef}
      className="fixed inset-x-3 top-16 z-40 max-h-[70dvh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:max-h-none sm:w-[min(20rem,calc(100vw-2rem))]"
      role="dialog"
      aria-label="系统通知"
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <BellIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <span className="text-sm font-semibold whitespace-nowrap">系统通知</span>
          {unread > 0 ? (
            <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-medium leading-4 text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </div>
        {unread > 0 ? (
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
            className="text-xs text-brand-600 hover:underline disabled:opacity-50 dark:text-brand-300 whitespace-nowrap"
          >
            {markAll.isPending ? '标记中…' : '全部已读'}
          </button>
        ) : null}
      </div>

      <div className="max-h-[calc(70dvh-3.25rem)] overflow-y-auto sm:max-h-96">
        {q.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> 加载中…
          </div>
        ) : q.isError ? (
          <div className="py-10 text-center text-xs text-red-600 dark:text-red-400">
            加载失败：{q.error?.message ?? '未知错误'}
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-xs text-slate-400 dark:text-slate-500">
            暂无通知
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((it) => {
              const isUnread = it.readAt == null;
              return (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(it)}
                    className={cn(
                      'flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60',
                      isUnread && 'bg-brand-50/40 dark:bg-brand-500/5',
                    )}
                  >
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                      {NOTIF_KIND_LABEL[it.kind]?.[0] ?? '系'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={cn(
                            'truncate text-sm',
                            isUnread
                              ? 'font-semibold text-slate-900 dark:text-slate-50'
                              : 'text-slate-700 dark:text-slate-200',
                          )}
                        >
                          {it.title}
                        </span>
                      </div>
                      {it.body ? (
                        <p className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
                          {it.body}
                        </p>
                      ) : null}
                      <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
                        {NOTIF_KIND_LABEL[it.kind]} · {formatRelative(it.createdAt)}
                      </div>
                    </div>
                    {isUnread ? (
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-500" aria-label="未读" />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-slate-100 px-4 py-2 text-center dark:border-slate-800">
        <button
          type="button"
          onClick={() => {
            void navigate('/app');
            onClose();
          }}
          className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 whitespace-nowrap"
        >
          查看仪表盘
        </button>
      </div>
    </div>
  );
}
